import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { api, getFullApiUrl } from '../services/api';
import { FiFilter, FiRefreshCw, FiCheck, FiX, FiImage, FiHash, FiChevronDown, FiChevronUp, FiZoomIn, FiChevronLeft, FiChevronRight, FiDownload, FiSquare, FiCheckSquare, FiTrash2, FiGrid, FiEdit3, FiClock, FiCamera, FiCopy, FiStar, FiVideo } from 'react-icons/fi';
import ContentRefineModal from '../components/ContentRefineModal';
import VersionHistoryModal from '../components/VersionHistoryModal';
import LogoPreviewModal from '../components/LogoPreviewModal';
import VideoGenerationModal from '../components/VideoGenerationModal';
import { QualityScoreBadge } from '../components/QualityScoreBadge';

// Lazy image loader component
function LazyImage({ ideaId, className, onClick }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    const loadImage = async () => {
      try {
        // Pass retry=true if this is a retry attempt (retryCount > 0)
        const response = await api.getContentImage(ideaId, retryCount > 0);
        
        if (!mounted) return;
        
        if (response && response.image_url) {
          // Transform GridFS URLs to full URLs
          setImageUrl(getFullApiUrl(response.image_url));
          setError(false);
        } else {
          setError(true);
        }
      } catch (err) {
        if (mounted) {
          if (retryCount < 1) {
            setRetryCount(prev => prev + 1);
          } else {
            setError(true);
          }
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setIsRetrying(false);
        }
      }
    };
    
    loadImage();
    
    return () => { mounted = false; };
  }, [ideaId, retryCount]);

  if (loading || isRetrying) {
    return (
      <div className={`${className} bg-muted/20 flex items-center justify-center flex-col gap-2`}>
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        {isRetrying && <span className="text-xs text-muted-foreground">Generating image...</span>}
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div className={`${className} bg-muted/20 flex items-center justify-center flex-col gap-2`}>
        <FiImage className="text-muted-foreground" size={32} />
        <button 
          onClick={() => { setLoading(true); setIsRetrying(true); setError(false); setRetryCount(prev => prev + 1); }}
          className="text-xs text-primary hover:underline"
          data-testid="retry-image-button"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <img 
      src={imageUrl} 
      alt="Generated content" 
      className={className}
      onClick={onClick}
    />
  );
}

function ContentLibrary() {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);  // Track pending items
  const [filters, setFilters] = useState({
    client_id: '',
    campaign_id: '',
    platform: '',
    status: ''
  });
  const [expandedCard, setExpandedCard] = useState(null);
  const [regenerating, setRegenerating] = useState(null);
  const [lightboxState, setLightboxState] = useState({ itemId: null, imageIndex: 0 });
  const [lightboxImageUrl, setLightboxImageUrl] = useState(null);
  const [lightboxLoading, setLightboxLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Refine modal state
  const [refineModalContent, setRefineModalContent] = useState(null);
  const [refineModalImageIndex, setRefineModalImageIndex] = useState(0);  // Which image in stack to refine
  const [loadingRefineImage, setLoadingRefineImage] = useState(null); // Track which item is loading
  
  // Version history modal state
  const [versionHistoryContent, setVersionHistoryContent] = useState(null);
  
  // Video generation modal state
  const [videoModalContent, setVideoModalContent] = useState(null);
  const [loadingVideoContent, setLoadingVideoContent] = useState(null);
  
  // Logo preview modal state
  const [logoPreviewState, setLogoPreviewState] = useState({ 
    isOpen: false, 
    contentId: null, 
    imageUrl: null,
    logoUrl: null,
    aiSuggestion: null,
    clientName: ''
  });
  const [applyingLogo, setApplyingLogo] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [loadingMore, setLoadingMore] = useState(false);

  // Get the current lightbox item from content array
  const lightboxItem = useMemo(() => {
    if (!lightboxState.itemId) return null;
    return content.find(item => item.id === lightboxState.itemId);
  }, [content, lightboxState.itemId]);

  // Get all images for the current content item in lightbox
  const getCurrentImages = useCallback(() => {
    if (!lightboxItem) return [];
    
    // If item has individual images array, use that (transform URLs)
    if (lightboxItem.images && lightboxItem.images.length > 0) {
      return lightboxItem.images.map(img => getFullApiUrl(img));
    }
    // Use cached lightbox URL if available, otherwise item's image_url
    if (lightboxImageUrl) return [lightboxImageUrl];
    if (lightboxItem.image_url) return [getFullApiUrl(lightboxItem.image_url)];
    return [];
  }, [lightboxItem, lightboxImageUrl]);

  // Load image when lightbox opens - always fetch fresh to show refined images
  useEffect(() => {
    if (!lightboxState.itemId) {
      setLightboxImageUrl(null);
      return;
    }
    
    const item = content.find(i => i.id === lightboxState.itemId);
    if (!item) return;
    
    // For multiple images, ALWAYS fetch fresh from API to ensure we have latest refined versions
    // The images array in state may be stale if the user refined an image
    if (item.images_count > 1 || (item.images && item.images.length > 1)) {
      setLightboxLoading(true);
      setLightboxImageUrl(null);
      
      // Fetch all images for this item - always fetch fresh to get refined images
      api.getContentImage(lightboxState.itemId, false, true) // all_images=true
        .then(response => {
          if (response && response.images && response.images.length > 0) {
            // Always update the content item with fetched images (to get refined versions)
            setContent(prev => {
              return prev.map(c => 
                c.id === lightboxState.itemId 
                  ? { ...c, images: response.images, image_url: response.image_url }
                  : c
              );
            });
            setLightboxImageUrl(getFullApiUrl(response.images[lightboxState.imageIndex || 0]));
          } else if (response && response.image_url) {
            setLightboxImageUrl(getFullApiUrl(response.image_url));
          }
        })
        .catch(err => console.error('Failed to load lightbox images:', err))
        .finally(() => setLightboxLoading(false));
      return;
    }
    
    // Single image - fetch from API
    setLightboxLoading(true);
    setLightboxImageUrl(null);
    
    api.getContentImage(lightboxState.itemId)
      .then(response => {
        if (response && response.image_url) {
          setLightboxImageUrl(getFullApiUrl(response.image_url));
        }
      })
      .catch(err => console.error('Failed to load lightbox image:', err))
      .finally(() => setLightboxLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxState.itemId, lightboxState.imageIndex]);

  // Get content index for navigation
  const currentContentIndex = useMemo(() => {
    if (!lightboxState.itemId) return -1;
    return content.findIndex(item => item.id === lightboxState.itemId);
  }, [content, lightboxState.itemId]);

  // Handle keyboard navigation in lightbox
  const handleKeyDown = useCallback((e) => {
    if (!lightboxState.itemId) return;
    
    const currentImages = getCurrentImages();
    
    if (e.key === 'Escape') {
      setLightboxState({ itemId: null, imageIndex: 0 });
    } else if (e.key === 'ArrowLeft') {
      // If multiple images in current item, navigate within them first
      if (currentImages.length > 1 && lightboxState.imageIndex > 0) {
        setLightboxState(prev => ({ ...prev, imageIndex: prev.imageIndex - 1 }));
      } else if (currentContentIndex > 0) {
        // Move to previous content item
        const prevItem = content[currentContentIndex - 1];
        const lastImageIndex = prevItem?.images?.length > 0 ? prevItem.images.length - 1 : 0;
        setLightboxState({ itemId: prevItem.id, imageIndex: lastImageIndex });
      }
    } else if (e.key === 'ArrowRight') {
      // If multiple images in current item, navigate within them first
      if (currentImages.length > 1 && lightboxState.imageIndex < currentImages.length - 1) {
        setLightboxState(prev => ({ ...prev, imageIndex: prev.imageIndex + 1 }));
      } else if (currentContentIndex < content.length - 1) {
        // Move to next content item
        const nextItem = content[currentContentIndex + 1];
        setLightboxState({ itemId: nextItem.id, imageIndex: 0 });
      }
    }
  }, [lightboxState, content, currentContentIndex, getCurrentImages]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (lightboxState.itemId) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxState.itemId]);

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    // Skip on initial mount - loadInitialData handles it
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }
    loadContent(1); // Reset to page 1 when filters change
  }, [filters]);

  const loadInitialData = async () => {
    try {
      const clientsData = await api.listClients();
      setClients(clientsData);
      
      // Load content (images are loaded lazily, so we can fetch more items)
      const contentResponse = await api.listContentIdeas({}, 1, 12);
      
      if (!contentResponse || !contentResponse.items) {
        setContent([]);
        setLoading(false);
        return;
      }
      
      // Filter rejected
      let filteredItems = [...contentResponse.items];
      filteredItems = filteredItems.filter(item => item.user_status !== 'rejected');
      
      // Load pending count
      const pendingResponse = await api.listContentIdeas({ status: 'pending' }, 1, 1);
      
      setContent(filteredItems);
      setPendingCount(pendingResponse?.total || 0);
      setPagination({
        page: contentResponse.page || 1,
        limit: contentResponse.limit || 12,
        total: contentResponse.total || 0,
        pages: contentResponse.pages || 0
      });
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadContent = async (page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    
    try {
      const activeFilters = {};
      if (filters.client_id) activeFilters.client_id = filters.client_id;
      if (filters.campaign_id) activeFilters.campaign_id = filters.campaign_id;
      if (filters.status) activeFilters.status = filters.status;
      
      const response = await api.listContentIdeas(activeFilters, page, 12);
      
      if (!response || !response.items) {
        setContent([]);
        return;
      }
      
      let filteredData = response.items || [];
      
      // Filter by platform on client side if needed
      if (filters.platform) {
        filteredData = filteredData.filter(item => item.platform === filters.platform);
      }
      
      // Hide rejected content by default (unless explicitly filtering for rejected)
      if (!filters.status) {
        filteredData = filteredData.filter(item => item.user_status !== 'rejected');
      }
      
      if (append) {
        setContent(prev => [...prev, ...filteredData]);
      } else {
        setContent(filteredData);
      }
      
      setPagination({
        page: response.page || page,
        limit: response.limit || 12,
        total: response.total || 0,
        pages: response.pages || 0
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setContent([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (pagination.page < pagination.pages && !loadingMore) {
      loadContent(pagination.page + 1, true);
    }
  };

  const loadCampaigns = async (clientId) => {
    if (!clientId) {
      setCampaigns([]);
      return;
    }
    try {
      const data = await api.listCampaigns(clientId);
      setCampaigns(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleClientChange = (e) => {
    const clientId = e.target.value;
    setFilters(prev => ({ ...prev, client_id: clientId, campaign_id: '' }));
    loadCampaigns(clientId);
  };

  const handleStatusChange = async (ideaId, status) => {
    try {
      await api.updateContentStatus(ideaId, status);
      // Update pending count when approving/rejecting
      const item = content.find(c => c.id === ideaId);
      if (item?.user_status === 'pending' && status !== 'pending') {
        setPendingCount(prev => Math.max(0, prev - 1));
      }
      loadContent();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleRegenerateImage = async (ideaId) => {
    setRegenerating(ideaId);
    try {
      await api.regenerateImage(ideaId);
      loadContent();
    } catch (error) {
      console.error('Error regenerating image:', error);
    } finally {
      setRegenerating(null);
    }
  };

  const toggleExpand = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  // Selection handlers for bulk actions
  const toggleSelectItem = (id) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === content.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(content.map(item => item.id)));
    }
  };

  const handleBulkAction = async (status) => {
    if (selectedItems.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      // Count how many pending items we're updating
      const pendingItemsBeingUpdated = content.filter(
        item => selectedItems.has(item.id) && item.user_status === 'pending'
      ).length;
      
      const promises = Array.from(selectedItems).map(id => 
        api.updateContentStatus(id, status)
      );
      await Promise.all(promises);
      
      // Update pending count
      if (status !== 'pending') {
        setPendingCount(prev => Math.max(0, prev - pendingItemsBeingUpdated));
      }
      
      setSelectedItems(new Set());
      loadContent();
    } catch (error) {
      console.error('Error with bulk action:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Download image handler - tracks download for AI learning
  const handleDownloadImage = async (imageUrl, filename, ideaId) => {
    // Track the download for AI learning (non-blocking)
    if (ideaId) {
      api.trackDownload(ideaId).catch(() => {});
    }
    
    // Handle base64 images
    if (imageUrl.startsWith('data:')) {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = filename || 'content-image.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // For regular URLs - need to handle CORS
      try {
        const res = await fetch(imageUrl, { mode: 'cors' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'content-image.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Download failed, trying fallback:', err);
        // Fallback: open in new tab for manual save
        window.open(imageUrl, '_blank');
      }
    }
  };

  // Copy text handler - tracks copy for AI learning
  const handleCopyText = async (text, ideaId, field) => {
    try {
      await navigator.clipboard.writeText(text);
      // Track the copy for AI learning (non-blocking)
      if (ideaId) {
        api.trackCopy(ideaId, field).catch(() => {});
      }
      return true;
    } catch (err) {
      console.error('Failed to copy:', err);
      return false;
    }
  };

  // Logo preview handlers
  const handleOpenLogoPreview = async (item) => {
    try {
      // Get client info for logo
      const clientInfo = clients.find(c => c.id === item.client_id);
      
      // Fetch the actual image URL (images are lazy-loaded from GridFS)
      let actualImageUrl = null;
      try {
        const imageResponse = await api.getContentImage(item.id);
        actualImageUrl = getFullApiUrl(imageResponse?.image_url);
      } catch (err) {
        console.warn('Could not fetch content image:', err);
      }
      
      if (!actualImageUrl) {
        alert('Could not load the content image. Please try again.');
        return;
      }
      
      // Find logo from client's brand assets (we need to fetch this)
      let logoUrl = null;
      try {
        const clientDetail = await api.getClient(item.client_id);
        const logoAsset = clientDetail.brand_assets?.find(a => a.asset_type === 'logo');
        logoUrl = getFullApiUrl(logoAsset?.image_url);
      } catch (err) {
        console.warn('Could not fetch client logo:', err);
      }
      
      if (!logoUrl) {
        // Show helpful message directing user to upload a logo
        alert(`No logo uploaded for ${clientInfo?.name || 'this brand'}. Go to the brand page to upload your logo first, then you can add it to your content.`);
        return;
      }
      
      // Use existing logo_suggestion if available, otherwise use smart defaults
      const suggestion = item.logo_suggestion || {
        position_x_percent: 88,
        position_y_percent: 92,
        size_percent: 0.12,
        opacity: 0.5,
        placement: 'bottom_right',
        reason: 'Default placement - adjust as needed'
      };
      
      setLogoPreviewState({
        isOpen: true,
        contentId: item.id,
        imageUrl: actualImageUrl,
        logoUrl: logoUrl,
        aiSuggestion: suggestion,
        clientName: clientInfo?.name || 'Brand'
      });
    } catch (err) {
      console.error('Failed to open logo preview:', err);
    }
  };

  const handleApplyLogo = async (settings) => {
    const contentId = logoPreviewState.contentId;
    if (!contentId) return;
    
    setApplyingLogo(contentId);
    try {
      const result = await api.applyLogo(contentId, settings);
      
      // Update the content in state with new image
      // Use getFullApiUrl to ensure GridFS URLs have the full backend prefix
      const fullImageUrl = getFullApiUrl(result.image_url);
      setContent(prev => prev.map(item => 
        item.id === contentId 
          ? { ...item, image_url: fullImageUrl, logo_applied: true }
          : item
      ));
      
      setLogoPreviewState({ isOpen: false, contentId: null, imageUrl: null, logoUrl: null, aiSuggestion: null, clientName: '' });
    } catch (err) {
      console.error('Failed to apply logo:', err);
    } finally {
      setApplyingLogo(null);
    }
  };

  const handleSkipLogo = async () => {
    const contentId = logoPreviewState.contentId;
    if (!contentId) return;
    
    try {
      await api.skipLogo(contentId);
      
      // Update the content in state
      setContent(prev => prev.map(item => 
        item.id === contentId 
          ? { ...item, logo_skipped: true }
          : item
      ));
      
      setLogoPreviewState({ isOpen: false, contentId: null, imageUrl: null, logoUrl: null, aiSuggestion: null, clientName: '' });
    } catch (err) {
      console.error('Failed to skip logo:', err);
    }
  };

  // Remove logo and restore original image
  const handleRemoveLogo = async (item) => {
    if (!window.confirm('Remove logo and restore original image?')) return;
    
    try {
      const result = await api.removeLogo(item.id);
      const fullImageUrl = getFullApiUrl(result.image_url);
      
      setContent(prev => prev.map(i => 
        i.id === item.id 
          ? { ...i, image_url: fullImageUrl, logo_applied: false, logo_skipped: false }
          : i
      ));
    } catch (err) {
      console.error('Failed to remove logo:', err);
      alert(err.response?.data?.detail || 'Failed to remove logo. The original image may not be available.');
    }
  };

  const handleApplyLogoToAll = async (settings) => {
    // Get all items that don't have logo applied yet (in the same client)
    const currentItem = content.find(i => i.id === logoPreviewState.contentId);
    const clientId = currentItem?.client_id;
    
    const itemsToApply = content.filter(item => 
      item.client_id === clientId && !item.logo_applied && !item.logo_skipped
    );
    
    for (const item of itemsToApply) {
      try {
        const result = await api.applyLogo(item.id, settings);
        const fullImageUrl = getFullApiUrl(result.image_url);
        setContent(prev => prev.map(i => 
          i.id === item.id 
            ? { ...i, image_url: fullImageUrl, logo_applied: true }
            : i
        ));
      } catch (err) {
        console.error(`Failed to apply logo to ${item.id}:`, err);
      }
    }
    
    setLogoPreviewState({ isOpen: false, contentId: null, imageUrl: null, logoUrl: null, aiSuggestion: null, clientName: '' });
  };

  const getPlatformColor = (platform) => {
    const colors = {
      instagram: 'bg-pink-500/10 text-pink-400',
      facebook: 'bg-blue-500/10 text-blue-400',
      youtube: 'bg-red-500/10 text-red-400'
    };
    return colors[platform] || 'bg-secondary text-muted-foreground';
  };

  const getStatusColor = (status) => {
    const colors = {
      approved: 'bg-emerald-500/10 text-emerald-400',
      rejected: 'bg-red-500/10 text-red-400',
      pending: 'bg-amber-500/10 text-amber-400',
      needs_review: 'bg-orange-500/10 text-orange-400'
    };
    return colors[status] || 'bg-secondary text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Loading content...</p>
        </div>
      </div>
    );
  }

  // Debug info - remove after fixing
  console.log('[ContentLibrary] Render - content.length:', content.length, 'loading:', loading);

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Content Library</h1>
          <p className="text-muted-foreground mt-1">View and manage all your generated content</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <FiFilter size={16} />
          <span>{content.length} of {pagination.total} ideas</span>
        </div>
      </div>

      {/* Pending Review Alert Banner */}
      {pendingCount > 0 && !filters.status && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-2.5 bg-primary/10 rounded-lg mr-4">
              <FiCheck className="text-primary" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">
                {pendingCount} content {pendingCount === 1 ? 'idea' : 'ideas'} awaiting your review
              </h3>
              <p className="text-sm text-muted-foreground">
                Review and approve/reject content to help AI learn your preferences
              </p>
            </div>
          </div>
          <button
            onClick={() => setFilters(prev => ({ ...prev, status: 'pending' }))}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium text-sm"
            data-testid="view-pending-btn"
          >
            Review Pending
          </button>
        </div>
      )}

      {/* Bulk Action Bar - appears when items are selected */}
      {selectedItems.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-2 text-primary hover:text-primary/80"
              data-testid="toggle-select-all"
            >
              {selectedItems.size === content.length ? (
                <FiCheckSquare size={20} />
              ) : (
                <FiSquare size={20} />
              )}
              <span className="font-medium">
                {selectedItems.size} selected
              </span>
            </button>
            <button
              onClick={() => setSelectedItems(new Set())}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleBulkAction('approved')}
              disabled={bulkActionLoading}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 transition-colors"
              data-testid="bulk-approve"
            >
              <FiCheck size={16} />
              <span>Approve All</span>
            </button>
            <button
              onClick={() => handleBulkAction('rejected')}
              disabled={bulkActionLoading}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
              data-testid="bulk-reject"
            >
              <FiTrash2 size={16} />
              <span>Reject All</span>
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card rounded-xl border border-border/50 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Client</label>
            <select
              value={filters.client_id}
              onChange={handleClientChange}
              className="w-full px-3 py-2.5 bg-secondary border border-border/50 rounded-lg text-sm text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
              data-testid="filter-client"
            >
              <option value="">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Campaign</label>
            <select
              value={filters.campaign_id}
              onChange={(e) => setFilters(prev => ({ ...prev, campaign_id: e.target.value }))}
              className="w-full px-3 py-2.5 bg-secondary border border-border/50 rounded-lg text-sm text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary/50 disabled:opacity-50 transition-all"
              disabled={!filters.client_id}
              data-testid="filter-campaign"
            >
              <option value="">All Campaigns</option>
              {campaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Platform</label>
            <select
              value={filters.platform}
              onChange={(e) => setFilters(prev => ({ ...prev, platform: e.target.value }))}
              className="w-full px-3 py-2.5 bg-secondary border border-border/50 rounded-lg text-sm text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
              data-testid="filter-platform"
            >
              <option value="">All Platforms</option>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="youtube">YouTube</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2.5 bg-secondary border border-border/50 rounded-lg text-sm text-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
              data-testid="filter-status"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="needs_review">Needs Review</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      {content.length === 0 ? (
        <div className="bg-card rounded-xl p-12 text-center border border-border/50">
          <FiImage className="mx-auto text-muted-foreground/30 mb-4" size={48} />
          <h3 className="text-lg font-heading font-semibold text-foreground mb-2">No Content Yet</h3>
          <p className="text-muted-foreground">Generate content from your campaigns to see it here.</p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {content.map((item, index) => (
            <div 
              key={item.id} 
              className={`bg-card rounded-xl border overflow-hidden transition-all hover-lift ${
                selectedItems.has(item.id) ? 'border-primary ring-2 ring-primary/20' : 'border-border/50 hover:border-primary/30'
              }`}
              data-testid={`content-card-${item.id}`}
            >
              {/* Image Section - Load images lazily */}
              {(item.image_url || item.has_image) && (
                <div className="relative group">
                  {/* Selection Checkbox */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSelectItem(item.id); }}
                    className={`absolute top-2 left-2 z-10 p-1.5 rounded-lg transition-all ${
                      selectedItems.has(item.id) 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-card/80 text-muted-foreground opacity-0 group-hover:opacity-100'
                    }`}
                    data-testid={`select-${item.id}`}
                  >
                    {selectedItems.has(item.id) ? <FiCheckSquare size={18} /> : <FiSquare size={18} />}
                  </button>

                  {/* Show image count badge if multiple images */}
                  {(item.images_count > 1 || (item.images && item.images.length > 1)) && (
                    <div className="absolute top-2 left-12 z-10 px-2 py-1 bg-black/70 text-white text-xs rounded-full flex items-center gap-1">
                      <FiGrid size={12} />
                      <span>{item.images_count || item.images?.length}</span>
                    </div>
                  )}

                  <div 
                    className="cursor-pointer"
                    onClick={() => {
                      setLightboxState({ itemId: item.id, imageIndex: 0 });
                    }}
                  >
                    {/* Use LazyImage for lazy loading - key includes version to force refetch after refinement */}
                    {item.image_url ? (
                      <img 
                        src={getFullApiUrl(item.image_url)} 
                        alt="" 
                        className="w-full h-48 object-cover"
                        loading="lazy"
                        data-testid={`image-${item.id}`}
                      />
                    ) : (
                      <LazyImage 
                        key={`${item.id}-v${item.version || 1}`}
                        ideaId={item.id}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center pointer-events-none">
                      <FiZoomIn 
                        className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" 
                        size={32}
                      />
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRegenerateImage(item.id); }}
                    disabled={regenerating === item.id}
                    className="absolute top-2 right-2 p-2 bg-card rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                    title="Regenerate Image"
                    data-testid={`regenerate-image-${item.id}`}
                  >
                    <FiRefreshCw className={`text-foreground ${regenerating === item.id ? 'animate-spin' : ''}`} size={16} />
                  </button>
                  
                  {/* Logo Preview Button - shows on ALL content that doesn't have logo applied/skipped */}
                  {!item.logo_applied && !item.logo_skipped && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleOpenLogoPreview(item); }}
                      className="absolute top-2 right-12 p-2 bg-yellow-500/90 text-white rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-yellow-600"
                      title="Add Brand Logo"
                      data-testid={`add-logo-${item.id}`}
                    >
                      <FiStar size={16} />
                    </button>
                  )}
                  
                  {/* Logo Applied Badge with Remove Option */}
                  {item.logo_applied && (
                    <div className="absolute bottom-2 right-2 flex items-center gap-1">
                      <div className="px-2 py-1 bg-emerald-500/90 text-white text-xs rounded-l-full flex items-center gap-1">
                        <FiStar size={10} />
                        <span>Logo</span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemoveLogo(item); }}
                        className="px-2 py-1 bg-red-500/80 hover:bg-red-600 text-white text-xs rounded-r-full transition-colors"
                        title="Remove Logo"
                        data-testid={`remove-logo-${item.id}`}
                      >
                        <FiX size={10} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Content Section */}
              <div className="p-4">
                {/* Needs Review Banner */}
                {item.status === 'needs_review' && (
                  <div className="mb-3 p-2 bg-orange-500/10 border border-orange-500/20 rounded-lg text-xs text-orange-400 flex items-center">
                    <FiRefreshCw className="mr-2 flex-shrink-0" size={14} />
                    <span>Campaign/Focus was updated. Review this content for relevance.</span>
                  </div>
                )}
                
                {/* Pending Review Banner - for new content */}
                {item.user_status === 'pending' && (
                  <div className="mb-3 p-2 bg-primary/10 border border-primary/30 rounded-lg text-xs text-primary flex items-center">
                    <FiCheck className="mr-2 flex-shrink-0" size={14} />
                    <span><strong>New content!</strong> Approve or reject to help AI learn your preferences.</span>
                  </div>
                )}
                
                {/* Tags */}
                <div className="flex items-center flex-wrap gap-2 mb-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getPlatformColor(item.platform)}`}>
                    {item.platform}
                  </span>
                  <span className="px-2 py-1 bg-secondary text-muted-foreground rounded text-xs">
                    {item.content_type?.replace(/_/g, ' ')}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(item.user_status || 'pending')}`}>
                    {item.user_status || 'pending'}
                  </span>
                  {/* Quality Score Badge */}
                  <QualityScoreBadge 
                    score={item.marketing_score}
                    size="sm"
                  />
                </div>
                
                {/* Score Verdict */}
                {item.verdict && (
                  <p className="text-xs text-primary mb-2 italic">💡 {item.verdict}</p>
                )}

                {/* Hook */}
                {item.hook && (
                  <div className="mb-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-muted-foreground uppercase">Hook</p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyText(item.hook, item.id, 'hook');
                        }}
                        className="p-1 text-muted-foreground hover:text-primary transition-colors"
                        title="Copy hook"
                        data-testid={`copy-hook-${item.id}`}
                      >
                        <FiCopy size={12} />
                      </button>
                    </div>
                    <p className="text-sm text-foreground font-medium">{item.hook}</p>
                  </div>
                )}

                {/* Caption (truncated) */}
                {item.caption && (
                  <div className="mb-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-muted-foreground uppercase">Caption</p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyText(item.caption, item.id, 'caption');
                        }}
                        className="p-1 text-muted-foreground hover:text-primary transition-colors"
                        title="Copy caption"
                        data-testid={`copy-caption-${item.id}`}
                      >
                        <FiCopy size={12} />
                      </button>
                    </div>
                    <p className="text-sm text-foreground/80 line-clamp-3">
                      {expandedCard === item.id ? item.caption : item.caption.slice(0, 150) + (item.caption.length > 150 ? '...' : '')}
                    </p>
                  </div>
                )}

                {/* Expand Button */}
                <button
                  onClick={() => toggleExpand(item.id)}
                  className="flex items-center text-xs text-primary hover:text-primary/80 mb-3"
                  data-testid={`expand-${item.id}`}
                >
                  {expandedCard === item.id ? (
                    <>
                      <FiChevronUp size={14} className="mr-1" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <FiChevronDown size={14} className="mr-1" />
                      Show More
                    </>
                  )}
                </button>

                {/* Expanded Content */}
                {expandedCard === item.id && (
                  <div className="space-y-3 border-t border-border pt-3">
                    {item.script && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase">Script</p>
                        <p className="text-sm text-foreground/80 whitespace-pre-wrap">{item.script}</p>
                      </div>
                    )}
                    
                    {item.visual_direction && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase flex items-center">
                          {item.is_photography_direction ? (
                            <>
                              <FiCamera size={12} className="mr-1 text-amber-500" />
                              📸 Photography Direction (for YOUR space)
                            </>
                          ) : (
                            'Visual Direction'
                          )}
                        </p>
                        <p className="text-sm text-foreground/80">{item.visual_direction}</p>
                      </div>
                    )}

                    {item.cta && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase">Call to Action</p>
                        <p className="text-sm text-foreground/80">{item.cta}</p>
                      </div>
                    )}

                    {item.hashtags && item.hashtags.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase flex items-center">
                          <FiHash size={12} className="mr-1" />
                          Hashtags
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {item.hashtags.map((tag, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {item.shot_list && item.shot_list.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase">Shot List</p>
                        <ul className="text-sm text-foreground/80 list-disc list-inside">
                          {item.shot_list.map((shot, idx) => (
                            <li key={idx}>{shot}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-border">
                  <button
                    onClick={() => handleStatusChange(item.id, 'approved')}
                    className={`flex-1 flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                      item.user_status === 'approved' 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                    }`}
                    data-testid={`approve-${item.id}`}
                  >
                    <FiCheck size={14} />
                    <span>Approve</span>
                  </button>
                  <button
                    onClick={() => handleStatusChange(item.id, 'rejected')}
                    className={`flex-1 flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                      item.user_status === 'rejected' 
                        ? 'bg-red-600 text-white' 
                        : 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                    }`}
                    data-testid={`reject-${item.id}`}
                  >
                    <FiX size={14} />
                    <span>Reject</span>
                  </button>
                  <button
                    onClick={async () => {
                      // Fetch image before opening refine modal (image_url excluded from list for performance)
                      setLoadingRefineImage(item.id);
                      try {
                        const imageResponse = await api.getContentImage(item.id);
                        if (imageResponse && imageResponse.image_url) {
                          setRefineModalContent({ ...item, image_url: imageResponse.image_url });
                        } else {
                          console.error('No image found for content:', item.id);
                          setRefineModalContent(item); // Let modal show error
                        }
                      } catch (err) {
                        console.error('Failed to load image for refine:', err);
                        setRefineModalContent(item); // Let modal show error
                      } finally {
                        setLoadingRefineImage(null);
                      }
                    }}
                    disabled={loadingRefineImage === item.id}
                    className={`flex-1 flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                      loadingRefineImage === item.id 
                        ? 'bg-primary/5 text-primary/50 cursor-wait' 
                        : 'bg-primary/10 text-primary hover:bg-primary/20'
                    }`}
                    data-testid={`refine-${item.id}`}
                  >
                    {loadingRefineImage === item.id ? (
                      <>
                        <div className="w-3 h-3 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <FiEdit3 size={14} />
                        <span>Refine</span>
                      </>
                    )}
                  </button>
                  {/* Video Generation Button */}
                  <button
                    onClick={async () => {
                      // Load content details for video generation
                      setLoadingVideoContent(item.id);
                      try {
                        // Get the full content including visual_direction
                        const imageResponse = await api.getContentImage(item.id);
                        setVideoModalContent({ 
                          ...item, 
                          image_url: imageResponse?.image_url 
                        });
                      } catch (err) {
                        console.error('Failed to load content for video:', err);
                      } finally {
                        setLoadingVideoContent(null);
                      }
                    }}
                    disabled={loadingVideoContent === item.id}
                    className={`flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                      loadingVideoContent === item.id
                        ? 'bg-purple-500/5 text-purple-400/50 cursor-wait'
                        : 'bg-purple-500/10 text-purple-400 hover:bg-purple-500/20'
                    }`}
                    data-testid={`video-${item.id}`}
                    title="Create video from this content"
                  >
                    {loadingVideoContent === item.id ? (
                      <>
                        <div className="w-3 h-3 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <FiVideo size={14} />
                        <span>Video</span>
                      </>
                    )}
                  </button>
                  {/* Version History Button - show if content has been refined */}
                  {item.version > 1 && (
                    <button
                      onClick={() => setVersionHistoryContent(item)}
                      className="flex items-center justify-center space-x-1 px-3 py-2 rounded-lg text-sm transition-colors bg-muted/50 text-muted-foreground hover:bg-muted"
                      data-testid={`versions-${item.id}`}
                      title={`${item.version} versions`}
                    >
                      <FiClock size={14} />
                      <span>v{item.version}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Load More Button */}
        {pagination.page < pagination.pages && (
          <div className="flex justify-center mt-8">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              data-testid="load-more-btn"
            >
              {loadingMore ? (
                <>
                  <FiRefreshCw className="animate-spin" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <span>Load More ({pagination.total - content.length} remaining)</span>
                </>
              )}
            </button>
          </div>
        )}
      </>
      )}

      {/* Image Lightbox Modal with Navigation - using Portal to render at body level */}
      {lightboxState.itemId && lightboxItem && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black"
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          data-testid="image-lightbox"
          onClick={() => setLightboxState({ itemId: null, imageIndex: 0 })}
        >
          {/* Top bar with close and download */}
          <div 
            className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-black/80 to-transparent flex items-center justify-between px-6"
            style={{ zIndex: 10000 }}
          >
            <div className="text-white font-medium">
              {lightboxItem.platform} · {lightboxItem.content_type?.replace(/_/g, ' ')}
            </div>
            <div className="flex items-center gap-2">
              {/* Version badge */}
              {lightboxItem.version > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxState({ itemId: null, imageIndex: 0 });
                    setVersionHistoryContent(lightboxItem);
                  }}
                  className="px-3 py-1.5 text-sm text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors flex items-center gap-1.5"
                  title="View version history"
                  data-testid="lightbox-version-badge"
                >
                  <FiClock size={14} />
                  <span>v{lightboxItem.version}</span>
                </button>
              )}
              {/* Refine button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const images = getCurrentImages();
                  const currentImageUrl = images[lightboxState.imageIndex];
                  // Close lightbox and open refine modal with current image
                  setLightboxState({ itemId: null, imageIndex: 0 });
                  setRefineModalImageIndex(lightboxState.imageIndex);
                  setRefineModalContent({ 
                    ...lightboxItem, 
                    images: images,
                    image_url: currentImageUrl 
                  });
                }}
                className="px-3 py-1.5 text-sm text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors flex items-center gap-1.5"
                title="Refine this image"
                data-testid="lightbox-refine-btn"
              >
                <FiEdit3 size={14} />
                <span>Refine</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const images = getCurrentImages();
                  const currentImage = images[lightboxState.imageIndex];
                  if (currentImage) {
                    handleDownloadImage(currentImage, `${lightboxItem.platform}-${lightboxItem.content_type}-${lightboxState.imageIndex + 1}.png`, lightboxItem.id);
                  }
                }}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Download Image"
                data-testid="download-image"
              >
                <FiDownload size={24} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxState({ itemId: null, imageIndex: 0 });
                }}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                data-testid="close-lightbox"
              >
                <FiX size={24} />
              </button>
            </div>
          </div>

          {/* Image container - centered using flexbox on parent */}
          <div 
            className="flex flex-col items-center"
            style={{ zIndex: 10001 }}
            onClick={(e) => e.stopPropagation()}
          >
            {lightboxLoading ? (
              <div className="w-64 h-64 flex items-center justify-center">
                <div className="w-12 h-12 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            ) : getCurrentImages().length > 0 ? (
              <img 
                src={getCurrentImages()[lightboxState.imageIndex]} 
                alt="Full size preview" 
                className="max-w-[85vw] max-h-[70vh] object-contain rounded-lg shadow-2xl"
              />
            ) : (
              <div className="w-64 h-64 flex items-center justify-center text-white/50">
                <FiImage size={48} />
              </div>
            )}
            
            {/* Thumbnail strip for multiple images */}
            {getCurrentImages().length > 1 && (
              <div className="flex items-center space-x-2 mt-4 p-3 bg-white/10 backdrop-blur-sm rounded-xl">
                {getCurrentImages().map((img, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxState(prev => ({ ...prev, imageIndex: idx }));
                    }}
                    className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                      lightboxState.imageIndex === idx ? 'border-white scale-110' : 'border-transparent opacity-50 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Previous button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              const currentImages = getCurrentImages();
              if (currentImages.length > 1 && lightboxState.imageIndex > 0) {
                setLightboxState(prev => ({ ...prev, imageIndex: prev.imageIndex - 1 }));
              } else if (currentContentIndex > 0) {
                const prevItem = content[currentContentIndex - 1];
                const lastImageIndex = prevItem?.images?.length > 0 ? prevItem.images.length - 1 : 0;
                setLightboxState({ itemId: prevItem.id, imageIndex: lastImageIndex });
              }
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            style={{ zIndex: 10001 }}
            data-testid="lightbox-prev"
          >
            <FiChevronLeft size={32} />
          </button>

          {/* Next button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              const currentImages = getCurrentImages();
              if (currentImages.length > 1 && lightboxState.imageIndex < currentImages.length - 1) {
                setLightboxState(prev => ({ ...prev, imageIndex: prev.imageIndex + 1 }));
              } else if (currentContentIndex < content.length - 1) {
                const nextItem = content[currentContentIndex + 1];
                setLightboxState({ itemId: nextItem.id, imageIndex: 0 });
              }
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
            style={{ zIndex: 10001 }}
            data-testid="lightbox-next"
          >
            <FiChevronRight size={32} />
          </button>

          {/* Bottom bar with navigation info */}
          <div 
            className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/80 to-transparent flex items-center justify-center"
            style={{ zIndex: 10000 }}
          >
            <p className="text-white/70 text-sm">
              {getCurrentImages().length > 1 
                ? `Image ${lightboxState.imageIndex + 1} of ${getCurrentImages().length} · Content ${currentContentIndex + 1} of ${content.length}`
                : `${currentContentIndex + 1} of ${content.length}`
              }
              <span className="text-white/40 ml-4">← → to navigate · Esc to close</span>
            </p>
          </div>
        </div>,
        document.body
      )}

      {/* Content Refine Modal */}
      {refineModalContent && (
        <ContentRefineModal
          content={refineModalContent}
          imageIndex={refineModalImageIndex}
          onClose={() => {
            setRefineModalContent(null);
            setRefineModalImageIndex(0);
          }}
          onSuccess={async (response, refinedImageIndex) => {
            setRefineModalContent(null);
            setRefineModalImageIndex(0);
            // Refresh content without showing loading spinner
            try {
              const activeFilters = {};
              if (filters.client_id) activeFilters.client_id = filters.client_id;
              if (filters.campaign_id) activeFilters.campaign_id = filters.campaign_id;
              if (filters.status) activeFilters.status = filters.status;
              
              const response = await api.listContentIdeas(activeFilters, 1, 20);
              let filteredData = response.items || [];
              
              if (filters.platform) {
                filteredData = filteredData.filter(item => item.platform === filters.platform);
              }
              if (!filters.status) {
                filteredData = filteredData.filter(item => item.user_status !== 'rejected');
              }
              
              setContent(filteredData);
              setPagination({
                page: response.page || 1,
                limit: response.limit || 20,
                total: response.total || 0,
                pages: response.pages || 0
              });
              console.log('[ContentLibrary] Refreshed after refine - items:', filteredData.length);
            } catch (error) {
              console.error('[ContentLibrary] Error refreshing after refine:', error);
            }
          }}
        />
      )}

      {/* Version History Modal */}
      {versionHistoryContent && (
        <VersionHistoryModal
          content={versionHistoryContent}
          onClose={async () => {
            setVersionHistoryContent(null);
            // Refresh content list to sync version numbers
            try {
              const activeFilters = {};
              if (filters.client_id) activeFilters.client_id = filters.client_id;
              if (filters.campaign_id) activeFilters.campaign_id = filters.campaign_id;
              if (filters.status) activeFilters.status = filters.status;
              
              const response = await api.listContentIdeas(activeFilters, pagination.page, 20);
              let filteredData = response.items || [];
              
              if (filters.platform) {
                filteredData = filteredData.filter(item => item.platform === filters.platform);
              }
              if (!filters.status) {
                filteredData = filteredData.filter(item => item.user_status !== 'rejected');
              }
              
              setContent(filteredData);
            } catch (error) {
              console.error('[ContentLibrary] Error refreshing after modal close:', error);
            }
          }}
          onRestore={async () => {
            setVersionHistoryContent(null);
            // Refresh content after restore
            try {
              const activeFilters = {};
              if (filters.client_id) activeFilters.client_id = filters.client_id;
              if (filters.campaign_id) activeFilters.campaign_id = filters.campaign_id;
              if (filters.status) activeFilters.status = filters.status;
              
              const response = await api.listContentIdeas(activeFilters, 1, 20);
              let filteredData = response.items || [];
              
              if (filters.platform) {
                filteredData = filteredData.filter(item => item.platform === filters.platform);
              }
              if (!filters.status) {
                filteredData = filteredData.filter(item => item.user_status !== 'rejected');
              }
              
              setContent(filteredData);
              setPagination({
                page: response.page || 1,
                limit: response.limit || 20,
                total: response.total || 0,
                pages: response.pages || 0
              });
              console.log('[ContentLibrary] Refreshed after version restore');
            } catch (error) {
              console.error('[ContentLibrary] Error refreshing after restore:', error);
            }
          }}
          onRefresh={async () => {
            // Refresh content list after version deletion (versions renumbered)
            try {
              const activeFilters = {};
              if (filters.client_id) activeFilters.client_id = filters.client_id;
              if (filters.campaign_id) activeFilters.campaign_id = filters.campaign_id;
              if (filters.status) activeFilters.status = filters.status;
              
              const response = await api.listContentIdeas(activeFilters, 1, 20);
              let filteredData = response.items || [];
              
              if (filters.platform) {
                filteredData = filteredData.filter(item => item.platform === filters.platform);
              }
              if (!filters.status) {
                filteredData = filteredData.filter(item => item.user_status !== 'rejected');
              }
              
              setContent(filteredData);
              console.log('[ContentLibrary] Refreshed after version delete');
            } catch (error) {
              console.error('[ContentLibrary] Error refreshing after delete:', error);
            }
          }}
        />
      )}
      
      {/* Logo Preview Modal */}
      <LogoPreviewModal
        isOpen={logoPreviewState.isOpen}
        onClose={() => setLogoPreviewState({ isOpen: false, contentId: null, imageUrl: null, logoUrl: null, aiSuggestion: null, clientName: '' })}
        imageUrl={logoPreviewState.imageUrl}
        logoUrl={logoPreviewState.logoUrl}
        aiSuggestion={logoPreviewState.aiSuggestion}
        onApply={handleApplyLogo}
        onSkip={handleSkipLogo}
        onApplyToAll={handleApplyLogoToAll}
        imageIndex={content.findIndex(c => c.id === logoPreviewState.contentId)}
        totalImages={(() => {
          const currentItem = content.find(i => i.id === logoPreviewState.contentId);
          return content.filter(c => c.client_id === currentItem?.client_id && !c.logo_applied && !c.logo_skipped).length;
        })()}
        clientName={logoPreviewState.clientName}
      />
      
      {/* Video Generation Modal */}
      {videoModalContent && (
        <VideoGenerationModal
          content={videoModalContent}
          onClose={() => setVideoModalContent(null)}
          onGenerate={(videoData) => {
            // For now, just log what would be sent to video generation API
            console.log('Video generation request:', videoData);
            alert(`Video prompt generated!\n\nPrompt: ${videoData.video_prompt?.substring(0, 200)}...\n\nCamera: ${videoData.camera_movement}\nDuration: ${videoData.duration}s\n\n(Video generation API will be integrated next)`);
            setVideoModalContent(null);
          }}
        />
      )}
    </div>
  );
}

export default ContentLibrary;
